import { useMemo } from "react";
import {
  extractSqlParamNames,
  type DashboardParameterValue,
  type DashboardWidget,
} from "../types";
import { isWidgetVisible } from "../views/parameters";

export interface UseWidgetDataSliceOptions {
  /**
   * When true, the caller (e.g. {@link WidgetGrid} with `resolvedParams`) has
   * already applied {@link isWidgetVisible}; skip the duplicate check.
   */
  visibilityPrechecked?: boolean;
}

/**
 * SQL + resolved-parameter slice for a widget, matching {@link DashboardWidgetTile}
 * so React Query keys align and we do not double-fetch.
 */
export function useWidgetDataSlice(
  widget: DashboardWidget,
  params: Record<string, DashboardParameterValue> | undefined,
  options?: UseWidgetDataSliceOptions,
) {
  const visible = options?.visibilityPrechecked ? true : isWidgetVisible(widget, params);
  const sql = visible && "sql" in widget ? widget.sql : null;

  const paramsSignature = useMemo(() => {
    if (!sql || !params) return null;
    const referenced = extractSqlParamNames(sql);
    if (referenced.length === 0) return null;
    const slice: Record<string, DashboardParameterValue> = {};
    for (const name of referenced) {
      if (name in params) slice[name] = params[name];
    }
    if (Object.keys(slice).length === 0) return null;
    const sorted = Object.keys(slice).sort();
    const canonical: Record<string, DashboardParameterValue> = {};
    for (const key of sorted) canonical[key] = slice[key];
    return JSON.stringify(canonical);
  }, [sql, params]);

  const widgetParams = useMemo(() => {
    if (paramsSignature === null) return undefined;
    return JSON.parse(paramsSignature) as Record<string, DashboardParameterValue>;
  }, [paramsSignature]);

  return { visible, sql, widgetParams };
}
